const Promise = require('bluebird');
const Reflux = require('@muzzylane/reflux');
const get = require('lodash/get');
const uuid = require('uuid');

const ForgeError = require('../util/error');
const ShortCourseActions = require('../actions/ShortCourseActions');
const ProjectActions = require('../actions/ProjectActions');

let shortCourses = {};
let docs = {};
let saves = {};
let editLocks = {};
let publishCompatibility = {};

let action = {};
let initialized = false;

let initializedChannels = {};
let initializedProjects = {};
let initializedShortCourses = {};
let courseCommentInfo = {};

let pendingGotoComment = {}; // what comment do we navigate to after editing a short course 

let courseLicenses = {};
let courseLicensesByCourseHash = {};

const ShortCourseStore = Reflux.createStore({
    listenables: [ShortCourseActions, ProjectActions],
    init: function() {},

    resetState: function() {
        shortCourses = {};
        saves = {};
        editLocks = {};
        publishCompatibility = {};
        initialized = false;
        initializedChannels = {};
        initializedProjects = {};
        initializedShortCourses = {};
        courseCommentInfo = {};
    },

    onSignOutCompleted: function(res) {
        this.resetState();
        this.emitStatus();
    },

    onSignOutFailed: function(err) {
        console.warn(err);
        this.resetState();
        this.emitStatus();
    },

    getState: function() {
        return {
            shortCourses: shortCourses,
            saves: saves,
            publishCompatibility: publishCompatibility,
            initialized: initialized,
            courseCommentInfo,
            action: action,
            courseLicenses: courseLicenses,
            courseLicensesByCourseHash: courseLicensesByCourseHash,
        };
    },

    getStateChannel: function(channelId) {
        return {
            initialized: this.getChannelInitialized(channelId),
            shortCourses: get(shortCourses, `${channelId}`, {}),
            publishCompatibility: publishCompatibility, // TODO filter for channel
            saves: saves, // TODO filter for channel
            action: action,
            courseLicenses: courseLicenses,
            courseLicensesByCourseHash: courseLicensesByCourseHash,
        }
    },

    getStateProject: function(projectId, channelId) {
        return {
            initialized: this.getProjectInitialized(projectId, channelId),
            shortCourses: get(shortCourses, `${channelId}.${projectId}`, {}),
            publishCompatibility: publishCompatibility, // TODO filter for project
            saves: saves, // TODO filter for project
            action: action,
            courseLicenses: courseLicenses,
            courseLicensesByCourseHash: courseLicensesByCourseHash,
        }
    },

    getStateShortCourse: function(shortCourseId, projectId, channelId) {
        return {
            initialized: this.getShortCourseInitialized(shortCourseId, projectId, channelId),
            shortCourse: get(shortCourses, `${channelId}.${projectId}.${shortCourseId}`, null),
            publishCompatibility: publishCompatibility[shortCourseId] || null,
            saves: get(saves, shortCourseId, null),
            editLock: editLocks[shortCourseId] || null,
            courseCommentInfo: courseCommentInfo[shortCourseId] || null,
            action: action,
            courseLicenses: courseLicenses,
            courseLicensesByCourseHash: courseLicensesByCourseHash,
        }
    },

    getShortCourseDoc: function(shortCourseId, version='latest') {
        return {
            doc: get(docs, `${shortCourseId}.${version}`, null),
        }
    },

    emitStatus: function() {
        this.trigger(this.getState());
        action = {};
    },

    getAndClearEditGotoComment(short_course_id) {
        var comment_id = pendingGotoComment[short_course_id];
        delete pendingGotoComment[short_course_id];
        return comment_id;
    },
    
    setEditGotoComment(short_course_id, comment_id) {
        pendingGotoComment[short_course_id] = comment_id;
    },

    setChannelInitialized(channelId, initialized=true) {
        const pChannelId = parseInt(channelId, 10);
        initializedChannels[pChannelId] = initialized;
    },

    setProjectInitialized(projectId, initialized=true) {
        const pProjectId = parseInt(projectId, 10);
        initializedProjects[pProjectId] = initialized;
    },

    setShortCourseInitialized(shortCourseId) {
        const pShortCourseId = parseInt(shortCourseId, 10);
        initializedShortCourses[pShortCourseId] = true;
    },

    getChannelInitialized(channelId) {
        const pChannelId = parseInt(channelId, 10);
        return initializedChannels[channelId];
    },

    getProjectInitialized(projectId, channelId) {
        const pProjectId = parseInt(projectId, 10);
        return this.getChannelInitialized(channelId) || initializedProjects[pProjectId];
    },

    getShortCourseInitialized(shortCourseId, projectId, channelId) {
        const pShortCourseId = parseInt(shortCourseId, 10);
        return this.getChannelInitialized(channelId) || this.getProjectInitialized(projectId) || initializedShortCourses[pShortCourseId];
    },

    onError: function(actionId, err) {
        action.id = actionId;
        if (err.status) {
            switch (err.status) {
                default:
                    action.error = new ForgeError({ message: err.responseText, stack: null }); // assume short course related requests provide user-facing error text
                    break;
            }
        }
        else {
            action.error = err;
        }

        this.emitStatus();
    },

    _mergeShortCourses(channelId, courseArr) {
        if (!shortCourses[channelId]) shortCourses[channelId] = {};

        courseArr.forEach((course) => {
            if (!shortCourses[channelId][course.project_id]) {
                shortCourses[channelId][course.project_id] = {};
            }
            let existingCourse = shortCourses[channelId][course.project_id][course.id];
            if (existingCourse) {
                course = { ...existingCourse, ...course }; // shallow merge so we do not remove existing data
            }
            shortCourses[channelId][course.project_id][course.id] = course;
        });
        
        // this.emitStatus(); // not sure why this was here?
    },

    _removeShortCourses(channelId, projectId, courseIdArr) {
        if (!shortCourses[channelId]) return;
        if (!shortCourses[channelId][projectId]) return;

        courseIdArr.forEach((courseId) => {
            if (!!shortCourses[channelId][projectId][courseId]) delete shortCourses[channelId][projectId][courseId];
        });
    },

    _mergeDoc(courseId, doc, isLatest=false) {
        if (!docs[courseId]) docs[courseId] = {};

        docs[courseId][doc.version] = doc;
        if (isLatest) {
            docs[courseId].latest = docs[courseId][doc.version];
        }
    },

    _removeDoc(courseId) {
        if (docs[courseId]) delete docs[courseId];
    },

    onGetShortCourseCompleted: function(actionId, channelId, courseObj) {
        action = { id: actionId };
        this._mergeShortCourses(channelId, [courseObj]);
        this.setShortCourseInitialized(courseObj.id);
        this.emitStatus();
    },

    onGetShortCourseFailed: function(actionId, channelId, err) {
        action = { id: actionId, error: err };
        this.emitStatus();
    },

	onGetChannelShortCoursesCompleted: function(actionId, channelId, courseArr) {
        action = { id: actionId };
        this._mergeShortCourses(channelId, courseArr);
        this.setChannelInitialized(channelId);
		this.emitStatus();
	},

	onGetChannelShortCoursesFailed: function(actionId, err) {
        action = { id: actionId, error: err };
        this.emitStatus();
	},

	onGetProjectShortCoursesCompleted: function(actionId, channelId, projectId, courseArr) {
        action = { id: actionId };
        this._mergeShortCourses(channelId, courseArr);
        this.setProjectInitialized(projectId);
        this.emitStatus();
	},

	onGetProjectShortCoursesFailed: function(actionId, err) {
        action = { id: actionId, error: err };
        this.emitStatus();
	},

	onGetShortCourseWithLatestDocCompleted: function(actionId, channelId, courseObj) {
        action = { id: actionId };
        this._mergeShortCourses(channelId, [courseObj]);
        this.setShortCourseInitialized(courseObj.id);
        this._mergeDoc(courseObj.id, courseObj.doc, true);
        this.emitStatus();
	},

	onGetShortCourseWithLatestDocFailed: function(actionId, channelId, err) {
        action = { id: actionId, error: err };
        this.emitStatus();
	},

    onCreateShortCourseCompleted: function(actionId, channelId, projectId, courseObj) {
        action = { id: actionId, newShortCourse: courseObj };
        this._mergeShortCourses(channelId, [courseObj]);
        this.emitStatus();
    },

    onCreateShortCourseFailed: function(actionId, channelId, projectId, err) {
        action = { id: actionId, error: err };
        this.emitStatus();
    },

    onCopyProjectCompleted: function(actionId, newProj) {
        action = { id: actionId };
        if (!shortCourses[newProj.channelId]) shortCourses[newProj.channelId] = {};

        shortCourses[newProj.channelId][newProj.id] = {};
        this.setProjectInitialized(newProj.id, false);
        ShortCourseActions.getProjectShortCourses(uuid.v4(), { projectId: newProj.id, channelId: newProj.channelId });
        this.emitStatus();
    },

    onGetShortCourseWithDocAtVersionCompleted: function(actionId, channelId, courseObj) {
        action = { id: actionId };
        this._mergeShortCourses(channelId, [courseObj]);
        this.setShortCourseInitialized(courseObj.id);
        this._mergeDoc(courseObj.id, courseObj.doc, courseObj.doc.version === courseObj.latest_version);
        this.emitStatus();
    },

    onGetShortCourseWithDocAtVersionFailed: function(actionId, channelId, err) {
        action = { id: actionId, error: err };
        this.emitStatus();
    },
    
    onAddShortCourseVersionAssetCompleted: function(actionId, params, res) {
        action = { id: actionId };
        this.emitStatus();
    },
    
    onAddShortCourseVersionAssetFailed: function(actionId, params, err) {
        action = { id: actionId, error: err };
        console.log("addShortCourseVersionAssetFailed",err);
        this.emitStatus();
    },

    onSaveShortCourseVersionCompleted: function(actionId, channelId, courseObj) {
        action = { id: actionId };
        this._mergeShortCourses(channelId, [courseObj]);
        this.setShortCourseInitialized(courseObj.id);
        this._mergeDoc(courseObj.id, courseObj.doc, true);
        this.emitStatus();
    },

    onSaveShortCourseVersionFailed: function(actionId, channelId, err) {
        action = { id: actionId, error: err };
        this.emitStatus();
    },

    onPublishShortCourseCompleted: function(actionId, courseObj) {
        action = { id: actionId };
        this._mergeShortCourses(courseObj.channel_id, [courseObj]);
        this.setShortCourseInitialized(courseObj.id);
        this._mergeDoc(courseObj.id, courseObj.doc, true);
        this.emitStatus();
    },

    onPublishShortCourseFailed: function(actionId, err) {
        action = { id: actionId, error: err };
        this.emitStatus();
    },

    onUpdateShortCourseCompleted: function(actionId, channelId, courseObj) {
        action = { id: actionId };
        this._mergeShortCourses(channelId, [courseObj]);
        this.setShortCourseInitialized(courseObj.id);
        this._mergeDoc(courseObj.id, courseObj.doc, true);
        this.emitStatus();
    },

    onUpdateShortCourseFailed: function(actionId, channelId, err) {
        action = { id: actionId, error: err };
        this.emitStatus();
    },

    onRenameShortCourseCompleted: function(actionId, channelId, courseObj) {
        this.onUpdateShortCourseCompleted(actionId, channelId, courseObj);
    },

    onRenameShortCourseFailed: function(actionId, channelId, err) {
        action = { id: actionId, error: err };
        this.emitStatus();
    },

    onArchiveShortCourseCompleted: function(actionId, channelId, projectId, courseId, res) {
        action = { id: actionId };
        this._removeShortCourses(channelId, projectId, [courseId]);
        this._removeDoc(courseId);
        this.emitStatus();
    },

    onArchiveShortCourseFailed: function(actionId, channelId, projectId, courseId, err) {
        action = { id: actionId, error: err };
        this.emitStatus();
    },

    onCopyShortCourseCompleted: function(actionId, shortCourseId, channelId, toProjectId, fromProjectId, courseObj) {
        action = { id: actionId };
        this._mergeShortCourses(channelId, [courseObj]);
        this.setShortCourseInitialized(courseObj.id);
        this._mergeDoc(courseObj.id, courseObj.doc, true);
        this.emitStatus();
    },

    onCopyShortCourseFailed: function(actionId, shortCourseId, channelId, toProjectId, fromProjectId, err) {
        this.onError(actionId, err);
    },

    onTransferShortCourseCompleted: function(actionId, shortCourseId, channelId, toProjectId, fromProjectId, courseObj) {
        action = { id: actionId };
        this._removeShortCourses(channelId, fromProjectId, [shortCourseId]);
        this._removeDoc(shortCourseId);
        this._mergeShortCourses(courseObj.channel_id, [courseObj]);
        this.setShortCourseInitialized(courseObj.id);
        this._mergeDoc(courseObj.id, courseObj.doc, true);
        this.emitStatus();
    },

    onTransferShortCourseFailed: function(actionId, shortCourseId, channelId, toProjectId, fromProjectId, err) {
        action = { id: actionId, error: err };
        this.emitStatus();
    },

    onGetShortCourseSavesCompleted: function(actionId, shortCourseId, res) {
        action = { id: actionId };
        saves[shortCourseId] = res;
        this.emitStatus();
    },

    onGetShortCourseSavesFailed: function(actionId, shortCourseId, err) {
        action = { id: actionId, error: err }
        this.emitStatus();
    },

    onUnpublishShortCourseCompleted: function(actionId, shortCourseId, channelId, courseObj) {
        action = { id: actionId };
        this._mergeShortCourses(channelId, [courseObj]);
        this.setShortCourseInitialized(courseObj.id);
        this._mergeDoc(courseObj.id, courseObj.doc, true);
        this.emitStatus();
    },
    
    onUnpublishShortCourseFailed: function(actionId, shortCourseId, channelId, err) {
        action = { id: actionId, error: err }
        this.emitStatus();
    },

    onGetEditLockStatusCompleted: function(actionId, projectId, shortCourseId, lockData) {
        // console.log('onGetEditLockStatusCompleted', actionId, projectId, shortCourseId, lockData);
        action = { id: actionId };
        editLocks[shortCourseId] = lockData;
        this.emitStatus();
    },

    onGetEditLockStatusFailed: function(actionId, projectId, shortCourseId, err) {
        console.warn('onGetEditLockStatusFailed', actionId, projectId, shortCourseId, err);
        action = { id: actionId, error: err };
        this.emitStatus();
    },

    onGetCommentInfoCompleted: function(actionId, shortCourseId, res) {
        action = { id: actionId };
        courseCommentInfo[shortCourseId] = res;
        this.emitStatus();
    },

    onGetCommentInfoFailed: function(actionId, shortCourseId, err) {
        console.warn('onGetCommentInfoFailed', actionId, shortCourseId, err);
        action = { id: actionId, error: err };
        this.emitStatus();
    },

    onUpdateCommentCompleted: function(actionId, params, res) {
        action = { id: actionId };
        this.emitStatus();
    },

    onUpdateCommentFailed: function(actionId, params, err) {
        console.warn('onUpdateCommentFailed', actionId, params, err);
        action = { id: actionId, error: err };
        this.emitStatus();
    },


    onPublishIsBreakingReset: function(shortCourseId) {
        if (publishCompatibility[shortCourseId]) delete publishCompatibility[shortCourseId];
        this.emitStatus();
    },

    onPublishIsBreakingCompleted: function(actionId, shortCourseId, res) {
        // console.log("onPublishIsBreakingCompleted");
        action = { id: actionId };
        publishCompatibility[shortCourseId] = res;
        this.emitStatus();
    },

    onPublishIsBreakingFailed: function(actionId, shortCourseId, err) {
        console.warn("onPublishIsBreakingFailed: ", actionId, shortCourseId, err);
        action = { id: actionId, error: err };
        this.emitStatus();
    },

    onGetLicensesForShortCourseCompleted: function(actionId, res) {
        action = { id: actionId };
        courseLicensesByCourseHash[res.hash] = {}; // reset this assuming we are getting all licenses for this short course
        res.licenses.forEach((license) => {
            courseLicenses[license.token] = license;
            courseLicensesByCourseHash[license.course_hash][license.token] = license;
        });
    
        this.emitStatus();
    },

    onGetLicensesForShortCourseFailed: function(actionId, err) {
        console.warn('onGetLicensesForShortCourseFailed', actionId, err);
        action = { id: actionId, error: err };
        this.emitStatus();
    },

    onGetShortCourseLicensesCompleted: function(actionId, res) {
        action = { id: actionId };
        res.forEach((license) => {
            courseLicenses[license.token] = license;
            if (!courseLicensesByCourseHash[license.course_hash]) {
                courseLicensesByCourseHash[license.course_hash] = {};
            }

            courseLicensesByCourseHash[license.course_hash][license.token] = license;
        });

        this.emitStatus();
    },

    onGetShortCourseLicensesFailed: function(actionId, err) {
        console.warn('onGetShortCourseLicensesFailed', actionId, err);
        action = { id: actionId, error: err };
        this.emitStatus();
    },

    onGetShortCourseLicenseCompleted: function(actionId, licenseToken, res) {
        action = { id: actionId };
        courseLicenses[res.token] = res;
        if (!courseLicensesByCourseHash[res.course_hash]) {
            courseLicensesByCourseHash[res.course_hash] = {};
        }

        courseLicensesByCourseHash[res.course_hash][res.token] = res;
        this.emitStatus();
    },
    
    onGetShortCourseLicenseFailed: function(actionId, licenseToken, err) {
        console.warn('onGetShortCourseLicenseFailed', actionId, licenseToken, err);
        action = { id: actionId, error: err };
        this.emitStatus();
    },

    onAddShortCourseLicenseCompleted: function(actionId, res) {
        action = { id: actionId };
        courseLicenses[res.token] = res;
        if (!courseLicensesByCourseHash[res.course_hash]) {
            courseLicensesByCourseHash[res.course_hash] = {};
        }

        courseLicensesByCourseHash[res.course_hash][res.token] = res;
        this.emitStatus();
    },

    onAddShortCourseLicenseFailed: function(actionId, err) {
        console.warn('onAddShortCourseLicenseFailed', actionId, err);
        action = { id: actionId, error: err };
        this.emitStatus();
    },
    
    onStartBuyShortCourseCheckoutCompleted: function(actionId, result) {
        action = { id: actionId };
        if (result.sessionId) {
            // stripe flow for embedded mode
            action.sessionId = result.sessionId;
        }
        else if (result.sessionUrl) {
            // stripe flow for hosted mode
            action.sessionUrl = result.sessionUrl;
        }
        else {
            console.log("onStartBuyShortCourseCheckoutCompleted got unexpected result",result);
        }
        this.emitStatus();
    },

    onStartBuyShortCourseCheckoutFailed: function(actionId, err) {
        console.warn('onAddShortCourseLicenseFailed', actionId, err);
        action = { id: actionId, error: err };
        this.emitStatus();
    },

    onUpdateShortCourseLicenseCompleted: function(actionId, prevToken, res) {
        action = { id: actionId };
        courseLicenses[res.token] = res;
        if (!courseLicensesByCourseHash[res.course_hash]) {
            courseLicensesByCourseHash[res.course_hash] = {};
        }

        if (prevToken !== res.token) {
            delete courseLicenses[prevToken];
            delete courseLicensesByCourseHash[res.course_hash][prevToken];
        }

        courseLicensesByCourseHash[res.course_hash][res.token] = res;
        this.emitStatus();
    },

    onUpdateShortCourseLicenseFailed: function(actionId, err) {
        console.warn('onUpdateShortCourseLicenseFailed', actionId, err);
        action = { id: actionId, error: err };
        this.emitStatus();
    },

    onDeleteShortCourseLicenseCompleted: function(actionId, courseHash, licenseToken, res) {
        action = { id: actionId };
        if (!courseLicensesByCourseHash[courseHash]) {
            courseLicensesByCourseHash[courseHash] = {};
        }

        delete courseLicenses[licenseToken];
        delete courseLicensesByCourseHash[courseHash][licenseToken];
        this.emitStatus();
    },

    onDeleteShortCourseLicenseFailed: function(actionId, courseHash, licenseToken, err) {
        console.warn('onDeleteShortCourseLicenseFailed', actionId, courseHash, licenseToken, err);
        action = { id: actionId, error: err };
        this.emitStatus();
    },

    onAdminAddLicenseForUserCompleted: function(actionId, res) {
        action = { id: actionId };
        // store just needs to register success of action
        this.emitStatus();
    },

    onAdminAddLicenseForUserFailed: function(actionId, err) {
        console.warn('onAdminAddLicenseForUserFailed', actionId, err);
        action = { id: actionId, error: err };
        this.emitStatus();
    },

//    // params: { input, context, history }
//    sendChatInput: function(params) {
//        if (!params.input) {
//            return Promise.resolve(''); // no input for chatbot
//        }
//
//        const queryString = $.param(params);  // Convert params to a query string
//        
//        return new Promise((resolve, reject) => {
//            $.ajax({
//                url: `/authoring/send_chat_input?${queryString}`,
//                type: 'GET',
//                contentType: 'application/json',
//            })
//            .then((res) => { 
//                resolve(res);
//            })
//            .fail((err) => {
//                console.log(`send_chat_input error`, err);
//                reject(err);
//            });
//        });
//    }, // sendChatInput

});

module.exports = ShortCourseStore;